import { ApolloError } from '@apollo/client';
import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
  Box,
  Flex,
  useToast,
} from '@chakra-ui/react';
import React from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';

import {
  GetCreditAgreementDocumentsMetadataDocumentGQLType,
  useCommitArtifactDocumentAndCreditAgreementsMutation,
} from '../implementations.gql.generated';
import { IconButtonV2 } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  errorToastConfig,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { UpdateFacilityNameModal } from '~/core/routes/credit-facilities/UpdateFacilityNameModal';
import { FacilityDetailsRouteParamsStruct } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/helper';
import {
  CreditAgreementProvider,
  useCreditAgreementStore,
} from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import { BreadCrumbs } from '~/core/routes/settings/implementations/layout/BreadCrumbs';
import { CreditAgreementHistoryDrawer } from '~/core/routes/settings/implementations/layout/CreditAgreementHistory/CreditAgreementHistoryDrawer';
import { NavMenu } from '~/core/routes/settings/implementations/layout/NavMenu/NavMenu';
import { SavingStatusTag } from '~/core/routes/settings/implementations/layout/SavingStatusTag';
import { CreditFacilityDocumentUploadDrawer } from '~/packages/credit-facility/ui/DocumentUpload/CreditFacilityDocumentUploadDrawer';

const ImplementationsLayoutBase = () => {
  const params = useParams();
  const flags = useFeatureFlags();
  const toast = useToast();

  const { facilityId } = FacilityDetailsRouteParamsStruct.parse(params);

  const [
    commitCreditAgreementMutation,
  ] = useCommitArtifactDocumentAndCreditAgreementsMutation();

  const commitCreditAgreement = async () => {
    try {
      const result = await commitCreditAgreementMutation({
        variables: {
          creditFacilityId: facilityId,
        },
        refetchQueries: [
          {
            query: GetCreditAgreementDocumentsMetadataDocumentGQLType,
            variables: {
              creditFacilityId: facilityId,
            },
          },
        ],
      });

      const errors =
        result.data?.commitArtifactDocumentAndCreditAgreements.errors;

      // Check for GraphQL errors in the response
      if (errors && errors.length > 0) {
        const errorMessage = errors[0];
        toast({
          ...errorToastConfig,
          title: 'Error occurred while committing credit agreement',
          description: errorMessage,
        });
        return;
      }

      toast({
        ...successToastConfig,
        title: 'Successfully committed credit agreement',
      });
    } catch (error) {
      // This will catch network errors, not GraphQL errors
      const errorMessage =
        error instanceof ApolloError ? error.message : 'Unknown error occurred';
      toast({
        ...errorToastConfig,
        title: 'Error occurred while committing credit agreement',
        description: errorMessage,
      });
    }
  };

  const menuControls = useDisclosure();
  const updateFacilityNameControls = useDisclosure();

  const isEditing = useCreditAgreementStore((store) => store.isEditing);
  const setIsEditing = useCreditAgreementStore((store) => store.setIsEditing);

  const capitalProviderOptions = useCreditAgreementStore(
    (store) =>
      store.creditAgreement?.facilityMechanics.parties.capitalProviders?.map(
        ({ entity }) => ({
          label: entity.name,
          value: entity.id,
        }),
      ) ?? [],
  );

  return (
    <Flex
      direction="column"
      position="relative"
      marginTop={['-1.5rem', '-1.5rem', '-1.5rem', '-2.5rem']} // Removes unwanted padding-top added by SettingsTemplate.tsx
    >
      <Flex gap="2rem" flex="1" alignItems="start">
        <Box position="sticky" top="0" alignSelf="flex-start" paddingTop="2rem">
          <NavMenu />
        </Box>
        <Flex direction="column" justifyContent="center" flex="1">
          <Flex
            py="2rem"
            background="white"
            zIndex="10"
            justify="space-between"
            alignItems="center"
            position="sticky"
            top="0"
            wrap="wrap"
          >
            <Flex mt="1rem" mb="1.5rem">
              <BreadCrumbs />
            </Flex>
            <Flex gap="1rem" alignItems="flex-end" marginLeft="auto">
              <SavingStatusTag />
              <CreditFacilityDocumentUploadDrawer
                capitalProviderOptions={capitalProviderOptions}
                creditFacilityId={facilityId}
              />
              <CreditAgreementHistoryDrawer />

              <IconButtonV2
                variant="primary-muted"
                icon={<Icon icon="bell" />}
                aria-label="Notifications"
                isDisabled // @implementation if we remove, consider the document upload permissions flag
              />
              <IconButtonV2
                variant="primary-muted"
                icon={<Icon icon="list-check" />}
                aria-label="Tasks"
                hasDotIndicator
                isDisabled // @implementation if we remove, consider the document upload permissions flag
              />
              {isEditing ? (
                <Button
                  colorScheme="v2-aurora"
                  variant="primary"
                  onClick={() => setIsEditing(false)}
                  isDisabled={
                    flags.productsImplementationPortalPermissions !== 'allow'
                  }
                >
                  Review Changes
                </Button>
              ) : (
                <>
                  <Button
                    colorScheme="v2-aurora"
                    variant="secondary"
                    onClick={() => setIsEditing(true)}
                    isDisabled={
                      flags.productsImplementationPortalPermissions !== 'allow'
                    }
                  >
                    Return to Editing
                  </Button>
                  {/* Add this button back when we have an approval flow */}
                  {/* <Button
                    colorScheme="v2-aurora"
                    variant="primary"
                    isDisabled={
                      flags.productsImplementationPortalPermissions !== 'allow'
                    }
                  >
                    Submit for Approval
                  </Button> */}
                </>
              )}

              <Menu {...menuControls} computePositionOnMount strategy="fixed">
                <MenuButton
                  isDisabled={
                    flags.productsImplementationPortalPermissions !== 'allow'
                  }
                  as={IconButtonV2}
                  variant="secondary-muted"
                  colorScheme="gray"
                  isActive={menuControls.isOpen}
                  aria-label="Options"
                  icon={<Icon icon="ellipsis-vertical" />}
                  tooltipEnabled={false}
                />
                <MenuList zIndex="dropdown">
                  <MenuItem onClick={commitCreditAgreement}>Commit</MenuItem>
                  <MenuItem onClick={updateFacilityNameControls.onOpen}>
                    <Icon icon="edit" style={{ marginRight: '0.75rem' }} />
                    Edit Facility Name
                  </MenuItem>
                </MenuList>
              </Menu>
              <UpdateFacilityNameModal
                modalControls={updateFacilityNameControls}
                preSelectedFacilityId={facilityId}
              />
            </Flex>
          </Flex>
          <Outlet />
        </Flex>
      </Flex>
    </Flex>
  );
};

export const ImplementationsLayout = () => (
  <RouteErrorBoundary
    fallback={({ message, showErrorToast }) => {
      showErrorToast(message);
      return <Navigate to="/settings/implementations" />;
    }}
  >
    <CreditAgreementProvider>
      <ImplementationsLayoutBase />
    </CreditAgreementProvider>
  </RouteErrorBoundary>
);
